import { fromJson, type JsonObject, type JsonValue } from '@bufbuild/protobuf';
import type { ErrorResponse } from '@/proto/agentplatform/httpv2/error_pb';
import { ErrorResponseSchema } from '@/proto/agentplatform/httpv2/error_pb';

/**
 * 常见 HTTP 状态码的通用中文兜底文案。key 为 envelope 的 `http_status_code`（数字）。
 *
 * 用状态码而非 `code` 字符串做 key：状态码是稳定契约，且能收敛同一状态的多个 code
 * 前缀（`validation.400` / `api.400` 都是 400），后端新增前缀也不会漏配。
 *
 * 优先级：本表按状态码的通用文案（可控、对用户友好，如「无权操作该资源」）> 后端
 * `data.message`（可能是英文技术原文，如「user id mismatch」）> `reason` / `code`。
 * 即状态码命中就用本表，没命中才回退后端 message，保证给用户看的是收敛后的中文文案。
 */
const STATUS_MESSAGES: ReadonlyMap<number, string> = new Map([
  [400, '请求参数有误'],
  [401, '登录已失效，请重新登录'],
  [403, '无权操作该资源'],
  [404, '资源不存在或已被删除'],
  [409, '资源冲突，请刷新后重试'],
  [429, '请求过于频繁，请稍后重试'],
  [500, '服务器开小差了，请稍后重试'],
  [502, '服务暂时不可用，请稍后重试'],
  [503, '服务暂时不可用，请稍后重试'],
  [504, '请求超时，请稍后重试'],
]);

/**
 * 依据 envelope 推导给用户看的文案。优先用本表按状态码的通用中文兜底文案（收敛、友好）；
 * 未配置该状态码时才回退到后端 message；再兜底 reason / code 原文，保证永不为空。
 */
function resolveMessage(resp: ErrorResponse): string {
  const statusMessage = STATUS_MESSAGES.get(resp.httpStatusCode);
  if (statusMessage) {
    return statusMessage;
  }
  return resp.data?.message?.trim() || resp.data?.reason?.trim() || resp.code;
}

/**
 * httpv2 业务错误。所有 httpv2 RPC 失败时 API 层抛出此异常，UI 可通过
 * `instanceof ApiError` 判断并读 code / reason / message / metadata。
 *
 * HTTP 透传层永远 200（除 503/504 由网络层产出）；业务状态由 envelope 内
 * `code` / `http_status_code` / `success_response` 表达，参见
 * docs/spec/rpc-spec.md §3。
 */
export class ApiError extends Error {
  readonly code: string; // e.g. "api.404" / "auth.403" / "validation.400" / "internal.500"
  readonly httpStatusCode: number; // 业务 HTTP 状态码，例如 404
  readonly reason: string;
  readonly detail: string; // 后端 data.message 原文（可能是英文技术细节），供需要展示具体原因的场景使用
  readonly metadata: Readonly<Record<string, string>>;

  constructor(resp: ErrorResponse, cortexReason = '') {
    super(resolveMessage(resp));
    this.name = 'ApiError';
    this.code = resp.code;
    this.httpStatusCode = resp.httpStatusCode;
    this.reason = resp.data?.reason ?? '';
    this.detail = cortexReason || resp.data?.message?.trim() || '';
    this.metadata = resp.data?.metadata ?? {};
  }
}

/**
 * 构造给用户看的完整错误文案：优先后端 `data.message`（ApiError.detail，携带真正原因，
 * 如「aliyun oss credentials invalid ... 403」），其后附上 `reason` 分类。detail 为空时
 * 回退到 message（按状态码收敛的中文兜底）。非 ApiError 直接取其 message。
 */
export function fullErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    const primary = e.detail || e.message;
    return e.reason ? `${primary}（${e.reason}）` : primary;
  }
  return e instanceof Error ? e.message : String(e);
}

/**
 * 从 httpv2 envelope 的 raw JSON 中识别业务错误，命中则返回 ApiError，否则 null。
 *
 * ⚠️ 判定必须用 `success_response !== true`，不能写 `=== false`：
 * protojson 默认省略 proto3 的 false 值，错误包络里 `success_response=false` 会被
 * 序列化为「字段缺失」。成功包络才显式带 `success_response: true`。写成 `=== false`
 * 会让错误响应漏判、被当成成功交给业务 fromJson，炸在未知字段（如 `reason`）上。
 */
export function tryParseEnvelopeError(raw: JsonValue): ApiError | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const obj = raw as JsonObject;
  // 只有携带 httpv2 envelope 结构（code + http_status_code）且非成功的响应才当业务错误，
  // 避免误伤 httpv1 裸响应。
  if (!('code' in obj) || !('http_status_code' in obj)) {
    return null;
  }
  if (obj.success_response === true) {
    return null;
  }
  const data = obj.data;
  const details = data && typeof data === 'object' && !Array.isArray(data) && data.error_details;
  const cortexReason =
    details && typeof details === 'object' && !Array.isArray(details)
      ? details.cortex_reason
      : undefined;
  return new ApiError(
    fromJson(ErrorResponseSchema, raw, { ignoreUnknownFields: true }),
    typeof cortexReason === 'string' ? cortexReason.trim() : ''
  );
}
